<template>
  <div class="pc-page__note" id="pc-page__note">
    <div class="pc-loading" v-if="isLoading">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>
    <template v-else>
      <div class="pc-page__container">
        <note-preview class="pc-note__container"/>
        <note-editor class="pc-note__container"/>
      </div>
      <tools/>
    </template>
  </div>
</template>

<script>
import NoteEditor from '@/components/note/editor/NoteEditor.vue'
import NotePreview from '@/components/note/preview/NotePreview.vue'
import Tools from '@/components/note/Tools.vue'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import key from 'keymaster'
import eventBus from '@/eventbus'
import { isNullOrWhiteSpace } from '@/common/utils'
import { operateError } from '@/common/errors'

export default {
  data () {
    return {
      KEY_SCOPE: 'keymaster-scope-note',
      autoSaveIntervalId: null
    }
  },
  components: {
    NoteEditor,
    Tools,
    NotePreview
  },
  created () {
    this.keyBind()
    this.load(this.$route.params.notebookId, this.$route.params.noteId)
    this.bindEventToWindowBeforeUnload()
    this.openAutoSave()
  },
  destroyed () {
    this.unbindEventToWindowBeforeUnload()
    this.closeAutoSave()
  },
  beforeRouteLeave (to, from, next) {
    if (this.isNoteContentChanged) {
      const ok = window.confirm('当前笔记已经被修改，离开将导致修改不被保存，是否继续？')
      if (ok) {
        this.keyUnbind()
        next()
      } else {
        next(false)
      }
    } else {
      this.keyUnbind()
      next()
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (this.isNoteContentChanged) {
      const ok = window.confirm('当前笔记已经被修改，离开将导致修改不被保存，是否继续？')
      if (ok) {
        this.load(to.params.notebookId, to.params.noteId)
        next()
      } else {
        next(false)
      }
    } else {
      this.load(to.params.notebookId, to.params.noteId)
      next()
    }
  },
  computed: {
    ...mapGetters('workbench/current', {
      isLoading: 'noteLoading',
      isNoteContentChanged: 'isNoteContentChanged',
      preview: 'preview'
    })
  },
  methods: {
    ...mapActions('workbench/current', {
      loadNotebookNote: 'loadNotebookNote',
      loadNote: 'loadNote',
      saveNoteContent: 'changeNoteContent'
    }),
    ...mapMutations('workbench/current', {
      setPreview: 'SET_PREVIEW'
    }),
    bindEventToWindowBeforeUnload () {
      const that = this
      window.onbeforeunload = (ev) => {
        if (that.isNoteContentChanged) {
          return 'stop'
        }
      }
    },
    unbindEventToWindowBeforeUnload () {
      window.onbeforeunload = null
    },
    async load (notebookId, noteId) {
      try {
        let noteContent
        if (notebookId) {
          noteContent = await this.loadNotebookNote({ notebookId, noteId })
        } else {
          noteContent = await this.loadNote({ noteId })
        }
        eventBus.$emit('WORKBENCH_CODEMIRROR_EDITOR_SET_VALUE', noteContent)
        if (this.preview) {
          if (isNullOrWhiteSpace(noteContent)) {
            this.setPreview(false)
          } else {
            eventBus.$emit('WORKBENCH_PREVIEW_UPDATE_HTML')
          }
        }
      } catch (e) {
        console.dir(e)
        operateError(e, '加载笔记')
      }
    },
    keyBind () {
      this.keyUnbind()
      key('ctrl+s', this.KEY_SCOPE, (event) => {
        this.save()
      })
      key('ctrl+alt+p', this.KEY_SCOPE, (event) => {
        this.setPreview(!this.preview)
      })
      key.setScope(this.KEY_SCOPE)
    },
    keyUnbind () {
      key.deleteScope(this.KEY_SCOPE)
    },
    async save () {
      try {
        await this.saveNoteContent()
      } catch (e) {
        console.dir(e)
        operateError(e, '保存笔记')
      }
    },
    openAutoSave () {
      const that = this
      this.autoSaveIntervalId = setInterval(() => {
        if (that.isNoteContentChanged) {
          that.saveNoteContent()
        }
      }, 30000)
    },
    closeAutoSave () {
      clearInterval(this.autoSaveIntervalId)
    }
  }
}
</script>

<style lang="scss">

.pc-page__note {
  width: 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.pc-page__container {
  flex-grow: 1;
}

.pc-note__container {
  max-width: 100rem;
  min-height: 20rem;
  margin: auto;
}
</style>
